import { KeycloakAuth } from '@aipb/gulliver/env/model';
import { Inject, Injectable, Optional } from '@angular/core';
import { addBreadcrumb } from '@sentry/angular';
import { KeycloakEvent, KeycloakEventType, KeycloakService } from 'keycloak-angular';
import { KeycloakLoginOptions } from 'keycloak-js';
import { from, Observable, tap } from 'rxjs';
import { filter, mergeMap } from 'rxjs/operators';
import { APP_REQUIRED_ROLES, AppRequiredRoles } from '../auth.model';
import { LoginSessionExpiredDialogService } from '../dialogs/login-session-expired-dialog';
import { AuthRepository } from '../state';
import { SnackbarService } from '@aipb/shared/util/snackbar';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private _defaultRoles: string[] = [];

  constructor(
    private keycloak: KeycloakService,
    private loginSessionExpiredDialogService: LoginSessionExpiredDialogService,
    private authRepository: AuthRepository,
    private snackbar: SnackbarService,
    @Optional() @Inject(APP_REQUIRED_ROLES) private _appRequiredRoles: AppRequiredRoles
  ) {
    // Try to update token every 65 seconds
    setInterval(() => {
      this.keycloak.updateToken(120);
    }, 65 * 1000);

    this.keycloak.keycloakEvents$
      .pipe(
        tap((evt: KeycloakEvent) => {
          if (evt.type === KeycloakEventType.OnAuthLogout) {
            this.snackbar.open('auth.user_logged_out', 'info');
            this.authRepository.setIsLoggedIn(false);
          }
        }),
        filter((evt: KeycloakEvent) => evt.type === KeycloakEventType.OnTokenExpired),
        mergeMap(() => this.keycloak.updateToken())
      )
      .subscribe({
        error: () => {
          this.loginSessionExpiredDialogService
            .open$()
            .pipe(filter((loginRequired) => loginRequired))
            .subscribe(() => {
              this.login();
            });
        }
      });
  }

  get appRequiredRoles(): AppRequiredRoles {
    return (
      this._appRequiredRoles || {
        gorazd: [],
        lana: [],
        merc: [],
        browser: [],
        catalog: [],
        explorer: [],
        fulltext: [],
        dashboard: [],
        'browser-mirador': [],
        'config-viewer': [],
        'dynamic-gallery': [],
        'keycloak-manager': [],
        'lana-tester': [],
        'personal-content': [],
        'report-tester': [],
        'customer-config-editor': [],
        'zip-manager': []
      }
    );
  }

  get token(): string {
    return this.keycloak.getKeycloakInstance().token;
  }

  init(config: KeycloakAuth): Observable<boolean> {
    if (config.defaultRoles) {
      this._defaultRoles = config.defaultRoles;
    }
    return from(
      this.keycloak.init({
        initOptions: {
          onLoad: 'check-sso',
          checkLoginIframe: false
        },
        loadUserProfileAtStartUp: true,
        config: {
          realm: config.realm,
          clientId: config.client_id,
          url: config.url || 'http://jarin.aipberoun.cz:7070/auth'
        },
        enableBearerInterceptor: false
      })
    ).pipe(
      tap((data) => {
        this.authRepository.setIsLoggedIn(data);
      })
    );
  }

  getUserId(): string {
    return this.keycloak.getKeycloakInstance().tokenParsed ? this.keycloak.getKeycloakInstance().tokenParsed.sub : null;
  }

  getUsername(): string {
    let username = '';

    try {
      username = this.keycloak.getUsername();
      // eslint-disable-next-line no-empty
    } catch (e) {}

    return username;
  }

  hasRole(role: string | ((role: string) => boolean)): boolean {
    if (typeof role === 'string') {
      return this.getUserRoles().includes(<string>role);
    }

    return this.getUserRoles().some(role);
  }

  getRole(role: string | ((role: string) => boolean)): string {
    if (typeof role === 'string') {
      return this.getUserRoles().find((r) => r === role);
    }

    return this.getUserRoles().find(role);
  }

  getUserRoles(): string[] {
    return [...this.keycloak.getUserRoles(true), ...Array.from(this._defaultRoles)];
  }

  hasGroup(group: string | ((group: string) => boolean)): boolean {
    if (typeof group === 'string') {
      return this.getGroups().includes(group);
    }

    return this.getGroups().some(group);
  }

  getGroups(): string[] {
    if (!this.keycloak?.getKeycloakInstance()?.tokenParsed) return [];

    return this.keycloak.getKeycloakInstance().tokenParsed['groups'];
  }

  login(options?: KeycloakLoginOptions): void {
    from(this.keycloak.login(options)).subscribe();
  }

  logout(): void {
    this.authRepository.clearPersist();
    from(this.keycloak.logout()).subscribe();
  }

  isLoggedIn(): boolean {
    return this.keycloak.getKeycloakInstance().authenticated;
  }

  isLoggedIn$(): Observable<boolean> {
    return this.authRepository.isLoggedIn$;
  }

  initSentryBreadcrumb() {
    addBreadcrumb({
      category: 'auth',
      message: this.isLoggedIn() ? `Authenticated [${this.getGroups()}][${this.getUserRoles()}]` : 'Anonymous',
      level: 'info'
    });
  }

  updateLoginRedirectUrl(url: string) {
    this.authRepository.setLoginRedirectUrl(url);
  }
}
